import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Typography } from '@mui/material'

import { clickableProps } from '../../helpers/interactive'
import { gameIconSx, linkTypographySx } from '../../helpers/styles'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAdDetail } from '../../types'
import { NetworkIcon } from '../NetworkIcon/NetworkIcon'

interface CreativeAdInfoStripProps {
  data: CreativeAdDetail
  /**
   * Pre-computed Ad Performance percentage (0-100), relative to the same
   * denominator the listing/card uses (the highest install share among the
   * game's creatives in the current network/month scope). Computed by the
   * parent dialog so the figure shown here always matches what the user
   * clicked from. `null` when there is no anchor to relate to.
   */
  adPerformancePct?: number | null
}

/**
 * Top-of-dialog info row showing game / network / impression share / creative id
 * / dominant motivation chips for a creative ad.
 */
export const CreativeAdInfoStrip: FC<CreativeAdInfoStripProps> = ({ data, adPerformancePct }) => {
  const { t } = useTranslation('ua-insights')
  const { openGame, openMotivation } = useUAInsightsActions()
  const { motivationLabel } = useUATaxonomy()

  return (
    <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {data.gameIcon && <Box component="img" src={data.gameIcon} alt={data.gameName} sx={gameIconSx(36)} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t('game')}
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={linkTypographySx} {...clickableProps(() => openGame(data.gameId))}>
            {data.gameName}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('network')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <NetworkIcon network={data.network} size={18} />
          <Typography variant="body1" fontWeight={600}>
            {data.network.name}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('col_ad_performance')}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ color: adPerformancePct != null ? 'text.primary' : 'text.disabled' }}>
          {adPerformancePct != null ? `${adPerformancePct.toFixed(0)}%` : '—'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('creative_id')}
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {data.id}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('associated_motivations')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {data.summary.dominantMotivations.map((m) => (
            <Chip key={m.id} label={motivationLabel(m)} size="small" color="primary" clickable onClick={() => openMotivation(m.id)} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
